import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { signOut } from 'firebase/auth';
import { auth } from '../lib/firebaseConfig';
import Logo from '../assets/images/sovo-logo.svg';

const functions = getFunctions(undefined, 'europe-central2');
const verifyPasswordResetToken = httpsCallable(functions, 'verifyPasswordResetToken');
const confirmPasswordReset = httpsCallable(functions, 'confirmPasswordReset');

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [checking, setChecking] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState(null);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setChecking(false);
      setTokenError('Brak tokenu. Poproś o nowy link.');
      return;
    }

    verifyPasswordResetToken({ token })
      .then((result) => {
        if (result.data?.valid) {
          setTokenValid(true);
        } else {
          setTokenError(result.data?.error || 'Nieprawidłowy link do resetowania hasła');
        }
      })
      .catch((err) => {
        setTokenError(err.message || 'Błąd weryfikacji linku');
      })
      .finally(() => setChecking(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Hasła nie są identyczne');
      return;
    }
    if (password.length < 8) {
      setError('Hasło musi mieć minimum 8 znaków');
      return;
    }

    setLoading(true);
    try {
      await confirmPasswordReset({ token, password });
      setSuccess(true);
      // Sign out and redirect to login
      try { await signOut(auth); } catch {}
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err) {
      setError(err.message || 'Nie udało się zmienić hasła');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="flex justify-center mb-4">
            <img src={Logo} alt="SOVO Logo" className="h-12 w-auto" />
          </div>

          <div className="flex items-center justify-center gap-1.5 mb-8">
            <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            <span className="text-sm text-gray-400">SOVO Pay</span>
          </div>

          {checking ? (
            <div className="text-center py-8">
              <svg className="animate-spin h-8 w-8 mx-auto text-[var(--maroon)]" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="mt-4 text-sm text-gray-600">Weryfikacja linku...</p>
            </div>
          ) : success ? (
            <div className="text-center space-y-4">
              <div className="mx-auto w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="font-semibold text-lg text-gray-900">Hasło zmienione!</h3>
              <p className="text-sm text-gray-600">
                Za chwilę zostaniesz przekierowany do logowania...
              </p>
            </div>
          ) : !tokenValid ? (
            <div className="text-center space-y-4">
              <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h3 className="font-semibold text-lg text-gray-900">Link nieprawidłowy</h3>
              <p className="text-sm text-gray-600">{tokenError}</p>
              <Link
                to="/forgot-password"
                className="inline-block w-full h-10 leading-10 px-4 bg-[var(--maroon)] text-white rounded-md text-sm font-medium hover:bg-[#5a0000]"
              >
                Poproś o nowy link
              </Link>
            </div>
          ) : (
            <>
              <h3 className="font-semibold text-lg text-gray-900 text-center mb-6">Ustaw nowe hasło</h3>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Nowe hasło (min. 8 znaków)
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    disabled={loading}
                    className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--maroon)] focus:border-[var(--maroon)] disabled:opacity-50"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                    Potwierdź hasło
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    disabled={loading}
                    className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--maroon)] focus:border-[var(--maroon)] disabled:opacity-50"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-10 px-4 py-2 bg-[var(--maroon)] text-white text-sm font-medium rounded-md hover:bg-[#5a0000] focus:outline-none focus:ring-2 focus:ring-[var(--maroon)] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Zapisywanie...' : 'Ustaw nowe hasło'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
